import React, { useEffect, useMemo, useState } from "react";
import { Loader2, PlusCircle, PlusSquare, Trash2, X } from "lucide-react";
import { authApi } from "../api";

const METAL_TYPES = ["GOLD", "SILVER", "PLATINUM"];
const PURITIES = ["24K", "22K", "18K", "14K", "92.5", "90.0"];
const CATEGORIES = ["Ring", "Necklace", "Bangle", "Bracelet", "Earring"];

const emptyBox = () => ({
  barcode: "",
  color_code: "",
  gross_weight: "",
  cover_weight: "",
  box_weight: "",
  net_weight: "",
  quantity: "",
});

const S = `
  .aos-overlay { position: fixed; inset: 0; background: rgba(15,23,42,.56); z-index: 1300; display: flex; align-items: center; justify-content: center; padding: 18px; }
  .aos-modal { width: min(980px, 100%); background: #fffdf8; border-radius: 22px; border: 1px solid #fde68a; box-shadow: 0 24px 60px rgba(15,23,42,.18); overflow: hidden; }
  .aos-head { display:flex; justify-content:space-between; align-items:center; padding:18px 22px; border-bottom:1px solid #fde68a; background: linear-gradient(135deg,#fff7ed,#fffbeb); }
  .aos-body { padding: 22px; display:grid; gap:16px; max-height: 80vh; overflow:auto; }
  .aos-grid { display:grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 14px; }
  .aos-box-grid { display:grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 10px; }
  .aos-field { display:grid; gap:6px; }
  .aos-label { font-size:12px; font-weight:700; color:#7c2d12; }
  .aos-input, .aos-select, .aos-textarea { width:100%; box-sizing:border-box; border:1px solid #fcd34d; border-radius:12px; padding:10px 12px; font-size:13px; color:#1f2937; background:#fff; }
  .aos-textarea { min-height: 90px; resize: vertical; }
  .aos-actions { display:flex; justify-content:flex-end; gap:10px; flex-wrap:wrap; margin-top: 6px; }
  .aos-btn { border:1px solid #fcd34d; border-radius:12px; padding:10px 14px; font-size:13px; font-weight:700; cursor:pointer; background:#fff; color:#7c2d12; display:inline-flex; align-items:center; gap:8px; }
  .aos-btn-primary { background: linear-gradient(135deg,#d97706,#b45309); color:#fff; border-color:#b45309; }
  .aos-error { border:1px solid #fecaca; background:#fff1f2; color:#b91c1c; padding:10px 12px; border-radius:12px; font-size:13px; }
  .aos-segment { display:flex; gap:8px; flex-wrap:wrap; }
  .aos-segment button { border:1px solid #fdba74; background:#fff; border-radius:999px; padding:9px 14px; font-size:12px; font-weight:800; cursor:pointer; color:#9a3412; }
  .aos-segment button.active { background:#f97316; color:#fff; border-color:#ea580c; }
  .aos-box-card { border:1px solid #fde68a; border-radius:16px; background:#fffbeb; padding:14px; display:grid; gap:10px; }
  .aos-box-head { display:flex; justify-content:space-between; align-items:center; gap:8px; }
  .aos-summary { border:1px dashed #f59e0b; border-radius:14px; padding:12px; background:#fff7ed; font-size:13px; color:#7c2d12; display:flex; gap:16px; flex-wrap:wrap; }
  .aos-dropdown { border:1px solid #fcd34d; border-radius:14px; background:#fff; max-height:180px; overflow:auto; }
  .aos-option { padding:10px 12px; border-bottom:1px solid #ffedd5; cursor:pointer; }
  .aos-option:last-child { border-bottom:none; }
  .aos-option:hover { background:#fff7ed; }
  @media (max-width: 860px) { .aos-grid, .aos-box-grid { grid-template-columns: 1fr; } }
`;

export default function AddOldStockModal({ open, onClose, onSuccess }) {
  const [form, setForm] = useState({
    design_id: null,
    design_code: "",
    design_name: "",
    metal_type: "GOLD",
    metal_purity: "22K",
    stock_entry_mode: "PIECES",
    quantity: 1,
    avg_piece_weight: "",
    unit_cost: "",
    category: "Ring",
    stone_details: "",
    remarks: "",
    boxes: [emptyBox()],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [designs, setDesigns] = useState([]);
  const [designSearch, setDesignSearch] = useState("");
  const [showDesigns, setShowDesigns] = useState(false);

  const parsedStoneDetails = useMemo(() => {
    if (!form.stone_details.trim()) return null;
    try {
      return JSON.parse(form.stone_details);
    } catch (_) {
      return "__INVALID__";
    }
  }, [form.stone_details]);

  const boxSummary = useMemo(() => {
    const rows = form.boxes || [];
    const totalBoxes = rows.length;
    const totalPieces = rows.reduce((sum, row) => sum + (parseInt(row.quantity, 10) || 0), 0);
    const totalNetWeight = rows.reduce((sum, row) => {
      const explicitNet = parseFloat(row.net_weight);
      if (!Number.isNaN(explicitNet) && explicitNet > 0) return sum + explicitNet;
      const gross = parseFloat(row.gross_weight) || 0;
      const cover = parseFloat(row.cover_weight) || 0;
      const boxWeight = parseFloat(row.box_weight) || 0;
      return sum + Math.max(0, gross - cover - boxWeight);
    }, 0);
    return { totalBoxes, totalPieces, totalNetWeight };
  }, [form.boxes]);

  useEffect(() => {
    if (!open) return;
    authApi.get("/job-cards/designs")
      .then((res) => setDesigns(res.data?.data || []))
      .catch(() => setDesigns([]));
  }, [open]);

  const filteredDesigns = useMemo(() => {
    const q = designSearch.trim().toLowerCase();
    if (!q) return designs.slice(0, 20);
    return designs.filter((design) => [design.design_code, design.design_name].filter(Boolean).join(" ").toLowerCase().includes(q)).slice(0, 20);
  }, [designs, designSearch]);

  if (!open) return null;

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const updateBox = (index, key, value) => setForm((prev) => ({
    ...prev,
    boxes: prev.boxes.map((box, i) => (i === index ? { ...box, [key]: value } : box)),
  }));
  const addBoxRow = () => setForm((prev) => ({ ...prev, boxes: [...prev.boxes, emptyBox()] }));
  const removeBoxRow = (index) => setForm((prev) => ({ ...prev, boxes: prev.boxes.filter((_, i) => i !== index) || [emptyBox()] }));

  const submit = async () => {
    setError("");
    if (parsedStoneDetails === "__INVALID__") {
      setError("Stone Details must be valid JSON if provided.");
      return;
    }
    setSaving(true);
    try {
      await authApi.post("/sales/store-inventory/add-old-stock", {
        design_id: form.design_id || null,
        design_code: form.design_code,
        design_name: form.design_name || null,
        metal_type: form.metal_type,
        metal_purity: form.metal_purity,
        stock_entry_mode: form.stock_entry_mode,
        quantity: form.stock_entry_mode === "PIECES" ? Number(form.quantity) : null,
        avg_piece_weight: form.stock_entry_mode === "PIECES" ? Number(form.avg_piece_weight) : null,
        unit_cost: form.unit_cost === "" ? null : Number(form.unit_cost),
        category: form.category || null,
        stone_details: parsedStoneDetails,
        remarks: form.remarks || null,
        boxes: form.stock_entry_mode === "BOXES" ? form.boxes.map((box) => ({
          barcode: box.barcode,
          color_code: box.color_code,
          gross_weight: box.gross_weight === "" ? null : Number(box.gross_weight),
          cover_weight: box.cover_weight === "" ? 0 : Number(box.cover_weight),
          box_weight: box.box_weight === "" ? 0 : Number(box.box_weight),
          net_weight: box.net_weight === "" ? null : Number(box.net_weight),
          quantity: box.quantity === "" ? null : Number(box.quantity),
        })) : null,
      });
      setForm({
        design_id: null,
        design_code: "",
        design_name: "",
        metal_type: "GOLD",
        metal_purity: "22K",
        stock_entry_mode: "PIECES",
        quantity: 1,
        avg_piece_weight: "",
        unit_cost: "",
        category: "Ring",
        stone_details: "",
        remarks: "",
        boxes: [emptyBox()],
      });
      setDesignSearch("");
      onSuccess?.();
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to add old stock.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="aos-overlay" onClick={onClose}>
      <style>{S}</style>
      <div className="aos-modal" onClick={(e) => e.stopPropagation()}>
        <div className="aos-head">
          <div>
            <div style={{ fontSize: 20, fontWeight: 900, color: "#7c2d12" }}>Add Old Stock</div>
            <div style={{ fontSize: 12, color: "#9a3412", marginTop: 4 }}>Add loose-piece stock or multiple customer-ready boxes with barcode and weight tracking.</div>
          </div>
          <button className="aos-btn" onClick={onClose}><X size={14} /> Close</button>
        </div>
        <div className="aos-body">
          {error ? <div className="aos-error">{error}</div> : null}

          <div className="aos-field">
            <label className="aos-label">Stock Entry Mode</label>
            <div className="aos-segment">
              <button className={form.stock_entry_mode === "PIECES" ? "active" : ""} onClick={() => update("stock_entry_mode", "PIECES")}>Pieces Wise</button>
              <button className={form.stock_entry_mode === "BOXES" ? "active" : ""} onClick={() => update("stock_entry_mode", "BOXES")}>Boxes Wise</button>
            </div>
          </div>

          <div className="aos-grid">
            <div className="aos-field">
              <label className="aos-label">Select Design From Master</label>
              <input
                className="aos-input"
                value={designSearch}
                placeholder="Search design code or design name"
                onFocus={() => setShowDesigns(true)}
                onChange={(e) => {
                  setDesignSearch(e.target.value);
                  setShowDesigns(true);
                }}
              />
              {showDesigns && filteredDesigns.length > 0 ? (
                <div className="aos-dropdown">
                  {filteredDesigns.map((design) => (
                    <div
                      key={design.design_id}
                      className="aos-option"
                      onClick={() => {
                        const inferredAvgWeight =
                          design.actual_weight ??
                          design.avg_piece_weight ??
                          design.metal_weight_g ??
                          "";
                        setForm((prev) => ({
                          ...prev,
                          design_id: design.design_id,
                          design_code: design.design_code || "",
                          design_name: design.design_name || "",
                          metal_type: design.metal_type || prev.metal_type,
                          metal_purity: design.purity || design.metal_purity || prev.metal_purity,
                          category: design.category || prev.category,
                          avg_piece_weight: inferredAvgWeight !== "" && inferredAvgWeight != null
                            ? String(inferredAvgWeight)
                            : prev.avg_piece_weight,
                        }));
                        setDesignSearch(`${design.design_code || ""} - ${design.design_name || ""}`.trim());
                        setShowDesigns(false);
                      }}
                    >
                      <div style={{ fontWeight: 800 }}>{design.design_code}</div>
                      <div style={{ fontSize: 12, color: "#64748b" }}>
                        {design.design_name || "-"}
                        {design.metal_type ? ` | ${design.metal_type}` : ""}
                        {design.purity || design.metal_purity ? ` | ${design.purity || design.metal_purity}` : ""}
                        {design.actual_weight || design.avg_piece_weight || design.metal_weight_g ? ` | ${design.actual_weight || design.avg_piece_weight || design.metal_weight_g} g` : ""}
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
            <div className="aos-field">
              <label className="aos-label">Selected Design Details</label>
              <div className="aos-summary">
                <span>Code: <strong>{form.design_code || "-"}</strong></span>
                <span>Name: <strong>{form.design_name || "-"}</strong></span>
                <span>Metal: <strong>{form.metal_type || "-"}</strong></span>
                <span>Purity: <strong>{form.metal_purity || "-"}</strong></span>
              </div>
            </div>
            <div className="aos-field">
              <label className="aos-label">Metal Type</label>
              <select className="aos-select" value={form.metal_type} onChange={(e) => update("metal_type", e.target.value)}>
                {METAL_TYPES.map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </div>
            <div className="aos-field">
              <label className="aos-label">Purity</label>
              <select className="aos-select" value={form.metal_purity} onChange={(e) => update("metal_purity", e.target.value)}>
                {PURITIES.map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </div>
            <div className="aos-field">
              <label className="aos-label">Cost per Piece</label>
              <input className="aos-input" type="number" min={0} step="0.01" value={form.unit_cost} onChange={(e) => update("unit_cost", e.target.value)} />
            </div>
            <div className="aos-field">
              <label className="aos-label">Category</label>
              <select className="aos-select" value={form.category} onChange={(e) => update("category", e.target.value)}>
                {CATEGORIES.map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </div>
          </div>

          {form.stock_entry_mode === "PIECES" ? (
            <div className="aos-grid">
              <div className="aos-field">
                <label className="aos-label">Pieces</label>
                <input className="aos-input" type="number" min={1} value={form.quantity} onChange={(e) => update("quantity", e.target.value)} />
              </div>
              <div className="aos-field">
                <label className="aos-label">Average Weight per Piece</label>
                <input className="aos-input" type="number" min={0} step="0.001" value={form.avg_piece_weight} onChange={(e) => update("avg_piece_weight", e.target.value)} />
              </div>
            </div>
          ) : (
            <div style={{ display: "grid", gap: 12 }}>
              <div className="aos-summary">
                <span>Total Boxes: <strong>{boxSummary.totalBoxes}</strong></span>
                <span>Total Pieces: <strong>{boxSummary.totalPieces}</strong></span>
                <span>Total Net Weight: <strong>{boxSummary.totalNetWeight.toFixed(3)} g</strong></span>
              </div>
              {(form.boxes || []).map((box, index) => (
                <div key={index} className="aos-box-card">
                  <div className="aos-box-head">
                    <strong style={{ color: "#9a3412" }}>Box {index + 1}</strong>
                    <button className="aos-btn" disabled={form.boxes.length === 1} onClick={() => removeBoxRow(index)}><Trash2 size={14} /> Remove</button>
                  </div>
                  <div className="aos-box-grid">
                    <div className="aos-field">
                      <label className="aos-label">Barcode</label>
                      <input className="aos-input" value={box.barcode} onChange={(e) => updateBox(index, "barcode", e.target.value)} />
                    </div>
                    <div className="aos-field">
                      <label className="aos-label">Color Code</label>
                      <input className="aos-input" value={box.color_code} onChange={(e) => updateBox(index, "color_code", e.target.value)} />
                    </div>
                    <div className="aos-field">
                      <label className="aos-label">Gross Weight</label>
                      <input className="aos-input" type="number" step="0.001" value={box.gross_weight} onChange={(e) => updateBox(index, "gross_weight", e.target.value)} />
                    </div>
                    <div className="aos-field">
                      <label className="aos-label">Cover Weight</label>
                      <input className="aos-input" type="number" step="0.001" value={box.cover_weight} onChange={(e) => updateBox(index, "cover_weight", e.target.value)} />
                    </div>
                    <div className="aos-field">
                      <label className="aos-label">Box Weight</label>
                      <input className="aos-input" type="number" step="0.001" value={box.box_weight} onChange={(e) => updateBox(index, "box_weight", e.target.value)} />
                    </div>
                    <div className="aos-field">
                      <label className="aos-label">Net Weight</label>
                      <input className="aos-input" type="number" step="0.001" value={box.net_weight} onChange={(e) => updateBox(index, "net_weight", e.target.value)} />
                    </div>
                    <div className="aos-field">
                      <label className="aos-label">Quantity</label>
                      <input className="aos-input" type="number" min={1} value={box.quantity} onChange={(e) => updateBox(index, "quantity", e.target.value)} />
                    </div>
                  </div>
                </div>
              ))}
              <div>
                <button className="aos-btn" onClick={addBoxRow}><PlusSquare size={14} /> Add Another Box</button>
              </div>
            </div>
          )}

          <div className="aos-field">
            <label className="aos-label">Stone Details JSON</label>
            <textarea className="aos-textarea" value={form.stone_details} onChange={(e) => update("stone_details", e.target.value)} placeholder='{"stone_type":"CZ","count":12,"carat":1.4}' />
          </div>
          <div className="aos-field">
            <label className="aos-label">Remarks</label>
            <textarea className="aos-textarea" value={form.remarks} onChange={(e) => update("remarks", e.target.value)} />
          </div>
          <div className="aos-actions">
            <button className="aos-btn" onClick={onClose}>Cancel</button>
            <button className="aos-btn aos-btn-primary" disabled={saving} onClick={submit}>
              {saving ? <Loader2 size={14} className="animate-spin" /> : <PlusCircle size={14} />} Save Old Stock
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
